import { Check, ChevronDown, Search } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  FORM_TRIGGER_CLASSES,
  FormSelectArrow,
} from '@/shared/components/ui/SearchSelect/formVariant';
import useCommonCodeSearch from '@/shared/hooks/useCommonCodeSearch';

interface CodeSearchSelectProps {
  // The cm_code group to search, e.g. 'ICD10'.
  upperCode: string;
  value: string;
  // Label for `value`. The selected code is usually outside the first page of
  // results, so its name has to come from whatever loaded the record.
  valueLabel?: string | null;
  onChange: (code: string) => void;
  disabled?: boolean;
  placeholder?: string;
  pageSize?: number;
  name?: string;
  className?: string;
  // 'form' = the plain <input>/<select> look of the other form fields (see
  // SearchSelect/formVariant.tsx), with a 14px regular-weight list. 'default'
  // keeps the large rounded look ReportDetails' form is built around.
  variant?: 'default' | 'form';
  // Sizing overrides for the trigger button (merged last, so they win).
  buttonClassName?: string;
}

/**
 * A select whose options are searched on the server, a page at a time.
 *
 * For cm_code groups too large for a plain `<select>`: ICD-10 is ~18,500
 * diagnoses, which is about a megabyte of JSON and 18,500 DOM nodes for a list
 * nobody scrolls. This loads the first page, then re-queries as the operator
 * types - every whitespace-separated token has to match the code or the name,
 * so "chol due" finds "Cholera due to vibrio cholerae".
 */
const CodeSearchSelect = ({
  upperCode,
  value,
  valueLabel,
  onChange,
  disabled = false,
  placeholder,
  pageSize = 50,
  name,
  className,
  variant = 'default',
  buttonClassName,
}: CodeSearchSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isForm = variant === 'form';

  // Nothing is fetched for a picker no one has opened. Staying enabled after
  // the first open keeps the cached page warm for the next one.
  const [hasOpened, setHasOpened] = useState(false);
  const { results, isLoading } = useCommonCodeSearch({
    upperCode,
    keyword,
    limit: pageSize,
    enabled: hasOpened,
  });

  // Prefer the name from the loaded page - it is in the operator's language and
  // current - and fall back to the label the record was loaded with.
  const selectedLabel = useMemo(() => {
    if (!value) return '';
    return results.list.find((item) => item.code === value)?.name ?? valueLabel ?? '';
  }, [results.list, value, valueLabel]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Closing drops the keyword: reopening on last session's search would show a
  // filtered list with no visible reason for it.
  useEffect(() => {
    if (!open) {
      setKeyword('');
      return;
    }
    setHighlight(0);
    searchRef.current?.focus();
  }, [open]);

  // Clamped rather than reset in an effect: a shorter page of results must not
  // leave the highlight pointing past the end of it.
  const activeIndex = Math.min(highlight, Math.max(results.list.length - 1, 0));

  const commit = (code: string) => {
    onChange(code);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const last = results.list.length - 1;
      const next =
        event.key === 'ArrowDown' ? Math.min(activeIndex + 1, last) : Math.max(activeIndex - 1, 0);
      setHighlight(next);
      listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = results.list[activeIndex];
      if (item) commit(item.code);
    }
  };

  // The list follows the trigger: 14px regular weight in a form, the original
  // 16px otherwise.
  const listText = isForm ? 'text-[14px] font-normal' : 'text-[16px]';

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setHasOpened(true);
          setOpen((previous) => !previous);
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 text-left',
          isForm
            ? [FORM_TRIGGER_CLASSES, disabled ? 'cursor-default' : 'cursor-pointer']
            : [
                'h-[45px] px-3 text-[20px] rounded-[12px] border bg-[#F6F6F6] dark:bg-[#F3F6FE]/10',
                'border-[#C2D2E2] dark:border-[#37424D]',
                disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
              ],
          buttonClassName
        )}
      >
        <span className="truncate">
          {value ? `${value}${selectedLabel ? ` - ${selectedLabel}` : ''}` : (placeholder ?? '')}
        </span>
        {isForm ? (
          <FormSelectArrow disabled={disabled} />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 opacity-60" />
        )}
      </button>

      {/* The real form value, so this behaves like the <select> it replaces. */}
      <input type="hidden" name={name} value={value} />

      {open && (
        <div
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-1 rounded-[12px] border shadow-lg',
            'bg-db-card border-[#C2D2E2] dark:border-[#37424D]'
          )}
        >
          <div className="flex items-center gap-2 p-2 border-b border-[#C2D2E2] dark:border-[#37424D]">
            <Search className="w-4 h-4 shrink-0 opacity-60" />
            <input
              ref={searchRef}
              type="text"
              value={keyword}
              placeholder={t('cmn > search')}
              onChange={(event) => {
                setKeyword(event.target.value);
                // A new search means a new list; keep the highlight on its top.
                setHighlight(0);
              }}
              onKeyDown={handleKeyDown}
              className={cn('w-full bg-transparent outline-none', listText)}
            />
          </div>

          <div ref={listRef} className="max-h-[240px] overflow-y-auto">
            {results.list.map((item, index) => (
              <button
                key={item.code}
                type="button"
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => {
                  // mousedown, not click: the search input's blur would close
                  // the popup before a click ever landed.
                  event.preventDefault();
                  commit(item.code);
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left',
                  listText,
                  index === activeIndex && 'bg-[#F3F6FE] dark:bg-[#F3F6FE]/10'
                )}
              >
                <span className={cn('shrink-0 w-[70px] truncate', !isForm && 'font-medium')}>
                  {item.code}
                </span>
                <span className={cn('truncate', !isForm && 'opacity-80')}>{item.name}</span>
                {item.code === value && <Check className="w-4 h-4 ml-auto shrink-0" />}
              </button>
            ))}

            {results.list.length === 0 && (
              <div className={cn('px-3 py-4 text-center opacity-60', listText)}>
                {isLoading ? t('cmn>master>loading') : t('msg > no data')}
              </div>
            )}
          </div>

          {/* Says outright that the list is a page, not the whole group - the
              difference between "no such diagnosis" and "keep typing". */}
          {/* {results.total_count > results.list.length && (
            <div className="px-3 py-2 border-t border-[#C2D2E2] dark:border-[#37424D] text-[14px] opacity-60">
              {results.list.length} / {results.total_count} {t('cmn > results')}
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

export default CodeSearchSelect;
